/**
 * Created by Daniel Wang on 3/1/2024.
 * Description: Component for add/edit general editor
 * ------ maintenance history ------
 */

import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { UntypedFormControl } from '@angular/forms';
import * as _lodash from 'lodash';

import { SlideSheetService } from '../../../../../services/slide-sheet.service';
import { SlideSheetActionTypes } from '../../../../slide-sheet/slide-sheet.model';
import { AppState } from '../../../../../redux';
import { Abbreviation, ConfigureHTMLSecurity, FMROverrides, GeneralGridType, StopList } from '../../../../../tamalelibs/models/admin-page.model';
import { AdminPageActionTypes } from '../../../../../redux/actions/admin-page.actions';
import { AdminPagesService } from '../../../../../tamalelibs/services/admin-page.service';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { EmailSignatureConfig } from '../../../../template/email-signature-content/email-signature-content.model';

export const ValidTags = ['<a>', '<abbr>', '<acronym>', '<address>', '<applet>', '<area>', '<b>', '<base>', '<basefont>', '<bdo>', '<bgsound>', '<big>', '<blockquote>', '<blink>', '<body>', '<br>', '<button>', '<caption>', '<center>', '<cite>', '<code>', '<col>', '<colgroup>', '<dd>', '<dfn>', '<del>', '<dir>', '<dl>', '<div>', '<dt>', '<embed>', '<em>', '<fieldset>', '<font>', '<form>', '<frame>', '<frameset>', '<h1>', '<h2>', '<h3>', '<h4>', '<h5>', '<h6>', '<head>', '<hr>', '<html>', '<iframe>', '<img>', '<input>', '<ins>', '<isindex>', '<i>', '<kbd>', '<label>', '<legend>', '<li>', '<link>', '<marquee>', '<menu>', '<meta>', '<noframe>', '<noscript>', '<optgroup>', '<option>', '<ol>', '<p>', '<pre>', '<q>', '<s>', '<samp>', '<script>', '<select>', '<small>', '<span>', '<strike>', '<strong>', '<style>', '<sub>', '<sup>', '<table>', '<td>', '<th>', '<tr>', '<tbody>', '<textarea>', '<tfoot>', '<thead>', '<title>', '<tt>', '<u>', '<ul>', '<var>'];

@Component({
    selector: 'tam-general-editor',
    templateUrl: './general-editor.component.html',
    styleUrls: ['./general-editor.component.scss']
})
export class GeneralEditorComponent implements OnInit, OnDestroy {
    @Input() data?: any;
    @Input() type?: GeneralGridType;

    // #region public properties
    name = new UntypedFormControl('');
    abbr = new UntypedFormControl('');
    canonicalForm = new UntypedFormControl('');
    fmrOverride = new UntypedFormControl('');
    ticker = new UntypedFormControl('');
    title = '';
    isConfigureHTMLSecurity = false;
    isAbbreviation = false;
    isFMROverride = false;
    isStopList = false;
    pageData: any;

    abbreviation = new Abbreviation();
    frmOverrides = new FMROverrides();
    // #endregion

    // #region private properties
    private _isEdit = false;
    // #endregion

    // #region constructor
    constructor(
        private _adminPageService: AdminPagesService,
        private _store: Store<AppState>,
        private _slideSheetService: SlideSheetService,
    ) { }
    // #endregion

    // #region angular functions
    ngOnInit() {
        this.isConfigureHTMLSecurity = this.type === GeneralGridType.ConfigureHTMLSecurity;
        this.isAbbreviation = this.type === GeneralGridType.Abbreviation;
        this.isFMROverride = this.type === GeneralGridType.FMROverrides;
        this.isStopList = this.type === GeneralGridType.StopList;

        if (this.data) {
            this.pageData = _lodash.cloneDeep(this.data);
            if (this.isConfigureHTMLSecurity) {
                this.title = 'Edit Html Security Tag';
                // this.name = this.data;
            } else if (this.isAbbreviation) {
                this.title = 'Edit abbreviation';
                // this.abbreviation = this.data;
            } else if (this.isFMROverride) {
                this.title = 'Edit FMR override';
                // this.frmOverrides = this.data;
            } else if (this.isStopList) {
                this.title = 'Edit stop list';
                // this.ticker = this.data;
            }

            this._isEdit = true;
        } else {
            if (this.isConfigureHTMLSecurity) {
                this.title = 'Create Html Security Tag';
                this.pageData = new ConfigureHTMLSecurity();
            } else if (this.isAbbreviation) {
                this.title = 'Create abbreviation';
                this.pageData = new Abbreviation();
            } else if (this.isFMROverride) {
                this.title = 'Create FMR override';
                this.pageData = new FMROverrides();
            } else if (this.isStopList) {
                this.title = 'Create stop list';
                this.pageData = new StopList();
            }

            // init controls
            this._initControls();
        }
    }

    ngOnDestroy(): void {
    }
    // #endregion

    // #region public functions
    onSlideSheetClose() {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.CLOSE
        });
    }

    /** Check the length of name. When the length of the name is valid, dispatches action to the store to create/edit object. */
    onSubmitClick() {
        this._adminPageService.checkGeneralName(this.type, this.pageData, this.data, this._isEdit).pipe(
            catchError((e: any) => {
                const mockResult = {
                    result: {
                        message: 'false' // to match server response. boolean will be better.
                    },
                };
                return of(mockResult);
            })
        ).subscribe(result => {
            if (result?.message === 'false') {
                if (this.isConfigureHTMLSecurity) {
                    this.name.setErrors({
                        nameDuplicated: true
                    });
                } else if (this.isAbbreviation) {
                    this.abbr.setErrors({
                        nameDuplicated: true
                    });
                } else if (this.isFMROverride) {
                    this.fmrOverride.setErrors({
                        nameDuplicated: true
                    });
                } else if (this.isStopList) {
                    this.ticker.setErrors({
                        nameDuplicated: true
                    });
                }
            } else {
                this._submit();
            }
        });
    }
    // #endregion

    // #region private functions
    private _initControls() {

    }

    private _isValid(): boolean {
        let isValid = true;

        if (this.isConfigureHTMLSecurity) {
            const htmlTagReg = /^<[^>]+>$/;
            this.name.setErrors(null);
            if (!this.name.value) {
                this.name.setErrors({
                    required: true
                });
                isValid = false;
            } else if (!htmlTagReg.test(this.pageData.tag.toLowerCase()) || !ValidTags.includes(this.pageData.tag.toLowerCase())) {
                this.name.setErrors({
                    invalidHTMLTag: true
                });
                isValid = false;
            }
        } else if (this.isAbbreviation) {
            this.abbr.setErrors(null);
            if (!this.abbr.value) {
                this.abbr.setErrors({
                    required: true
                });
                isValid = false;
            } else if (this.abbr.value.length > 128) {
                this.abbr.setErrors({
                    maxlength: true
                });
                isValid = false;
            }
            this.canonicalForm.setErrors(null);
            if (!this.canonicalForm.value) {
                this.canonicalForm.setErrors({
                    required: true
                });
                isValid = false;
            } else if (this.canonicalForm.value.length > 128) {
                this.canonicalForm.setErrors({
                    maxlength: true
                });
                isValid = false;
            }
        } else if (this.isFMROverride) {
            this.fmrOverride.setErrors(null);
            if (!this.fmrOverride.value) {
                this.fmrOverride.setErrors({
                    required: true
                });
                isValid = false;
            } else if (this.fmrOverride.value.length > 128) {
                this.fmrOverride.setErrors({
                    maxlength: true
                });
                isValid = false;
            }
        } else if (this.isStopList) {
            this.ticker.setErrors(null);
            if (!this.ticker.value) {
                this.ticker.setErrors({
                    required: true
                });
                isValid = false;
            } else if (this.ticker.value.length > 128) {
                this.ticker.setErrors({
                    maxlength: true
                });
                isValid = false;
            }
        }

        return isValid;
    }

    private _submit() {
        if (this._isValid()) {
            if (this._isEdit) {
                this._store.dispatch({
                    type: AdminPageActionTypes.EDIT_GENERAL_PAGE,
                    payload: {
                        type: this.type,
                        item: this.pageData,
                        oldItem: this.data
                    }
                });
            } else {
                if (this.type === GeneralGridType.ConfigureHTMLSecurity) {
                    const tag = this.pageData['tag'];
                    this._store.dispatch({
                        type: AdminPageActionTypes.CREATE_GENERAL_PAGE,
                        payload: {
                            type: this.type,
                            item: tag
                        }
                    });
                } else {
                    delete this.pageData['editable'];
                    delete this.pageData['deletable'];
                    this._store.dispatch({
                        type: AdminPageActionTypes.CREATE_GENERAL_PAGE,
                        payload: {
                            type: this.type,
                            item: this.pageData
                        }
                    });
                }
            }
        }
    }
    // #endregion
}
